import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Plus, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { templatesAPI, getErrorMessage } from '@/services/api';

export type QuickChipContentType = 'post' | 'story' | 'reel' | 'igtv' | 'carousel' | 'short';

export interface TemplateQuickChipsProps {
  currentText: string;
  platform?: string;
  /**
   * Active content type (Post/Story/Reel/Carousel/Short) in the composer.
   * Used to filter chip categories down to ones that make sense for that
   * format (e.g. Stories hide hashtags + first comment since those don't
   * render on Stories). Defaults to 'post'.
   */
  contentType?: QuickChipContentType;
  onApply: (result: {
    text: string;
    first_comment?: string;
    hashtags: string[];
  }) => void;
}

interface ApplyTemplateResult {
  text: string;
  first_comment?: string;
  hashtags: string[];
}

/** Canonical list of chip categories with their i18n keys + fallback labels. */
const CHIP_CATEGORIES = [
  { key: 'hook', i18nKey: 'templates_quick_hook', label: 'Hook' },
  { key: 'cta', i18nKey: 'templates_quick_cta', label: 'CTA' },
  { key: 'hashtags', i18nKey: 'templates_quick_hashtags', label: 'Hashtags' },
  { key: 'first_comment', i18nKey: 'templates_quick_first_comment', label: 'First Comment' },
] as const;

/**
 * Which chips to show per content type.
 * Stories don't render hashtags and have no comments — filter those out.
 * Other formats keep the full set.
 */
const CATEGORIES_BY_CONTENT_TYPE: Record<QuickChipContentType, readonly string[]> = {
  post: ['hook', 'cta', 'hashtags', 'first_comment'],
  story: ['hook', 'cta'],
  reel: ['hook', 'cta', 'hashtags', 'first_comment'],
  igtv: ['hook', 'cta', 'hashtags', 'first_comment'],
  carousel: ['hook', 'cta', 'hashtags', 'first_comment'],
  short: ['hook', 'cta', 'hashtags', 'first_comment'],
};

export const TemplateQuickChips: React.FC<TemplateQuickChipsProps> = ({
  currentText,
  platform,
  contentType = 'post',
  onApply,
}) => {
  const { t } = useTranslation();
  const [loadingCategory, setLoadingCategory] = useState<string | null>(null);
  const [seeded, setSeeded] = useState(false);

  // Show only the chip categories that apply to the active content type.
  const visibleCategories = React.useMemo(() => {
    const allowed = CATEGORIES_BY_CONTENT_TYPE[contentType] || CATEGORIES_BY_CONTENT_TYPE.post;
    return CHIP_CATEGORIES.filter((c) => allowed.includes(c.key));
  }, [contentType]);

  const applyMutation = useMutation({
    mutationFn: async (category: string) => {
      const response = await templatesAPI.getTopByCategory(category, {
        existing_text: currentText,
        platform,
      });
      return response.data as ApplyTemplateResult;
    },
    onSuccess: (data) => {
      onApply(data);
      toast.success(t('template_applied', 'Template applied'));
      setLoadingCategory(null);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: async (error: any, category: string) => {
      const errorMsg = error?.response?.data?.detail;

      // If no templates exist and we haven't seeded yet, auto-create starters
      if (errorMsg?.includes('No templates') && !seeded) {
        try {
          toast.loading(t('seeding_templates', 'Creating starter templates...'), { id: 'seed' });
          await templatesAPI.seedDefaults(true);
          setSeeded(true);
          toast.success(t('templates_seeded', 'Starter templates created!'), { id: 'seed' });

          // Retry the apply after seeding
          const response = await templatesAPI.getTopByCategory(category, {
            existing_text: currentText,
            platform,
          });
          const data = response.data as ApplyTemplateResult;
          onApply(data);
          toast.success(t('template_applied', 'Template applied'));
        } catch {
          toast.dismiss('seed');
          toast.error(
            t('seed_failed', 'Could not create templates. Try creating one manually.')
          );
        }
      } else if (errorMsg?.includes('No templates')) {
        toast.error(
          t('no_template_category', 'No templates in this category yet')
        );
      } else {
        toast.error(
          getErrorMessage(error, t('apply_error', 'Failed to apply template'))
        );
      }
      setLoadingCategory(null);
    },
  });

  const handleApplyCategory = (category: string) => {
    setLoadingCategory(category);
    applyMutation.mutate(category);
  };

  return (
    <div className="flex gap-2 overflow-x-auto py-2 px-4">
      {visibleCategories.map(({ key, i18nKey, label }) => (
        <button
          key={key}
          onClick={() => handleApplyCategory(key)}
          disabled={loadingCategory !== null}
          className="group relative whitespace-nowrap rounded-full bg-n-blue-bg px-3 py-1.5 text-xs font-semibold text-n-blue hover:opacity-80 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
          aria-label={t(`apply_${key}`, `Apply ${label}`)}
        >
          {loadingCategory === key ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              {t('loading', 'Loading...')}
            </>
          ) : (
            <>
              <Plus size={14} aria-hidden="true" />
              {t(i18nKey, label)}
            </>
          )}
        </button>
      ))}
    </div>
  );
};

export default TemplateQuickChips;
